import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import type { EntitlementsSupportOverviewDto } from '@/types/dtos/entitlements/support-overview.dto';

export const ENTITLEMENTS_SUPPORT_OVERVIEW_QUERY_KEY = 'entitlements-support-overview';

export function isLikelyObjectId(value: string): boolean {
  return /^[a-f0-9]{24}$/i.test(value.trim());
}

export async function fetchEntitlementsSupportOverview({
  scopeType,
  scopeId,
  token,
}: {
  scopeType: string;
  scopeId: string;
  token: string;
}): Promise<EntitlementsSupportOverviewDto> {
  const url = new URL(`${import.meta.env.VITE_API_URL}/entitlements/support/overview`);
  url.searchParams.set('scopeType', scopeType);
  url.searchParams.set('scopeId', scopeId.trim());
  return sendJSONRequest({ url: url.toString(), method: METHOD.GET, token }) as Promise<EntitlementsSupportOverviewDto>;
}

export function useEntitlementsSupportOverview(
  scopeType: string | undefined,
  scopeId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { getToken } = useAuth();
  const st = scopeType?.trim() ?? '';
  const sid = scopeId?.trim() ?? '';
  const idOk = isLikelyObjectId(sid);
  const enabled = (options?.enabled ?? true) && !!st && idOk;

  return useQuery({
    queryKey: [ENTITLEMENTS_SUPPORT_OVERVIEW_QUERY_KEY, st, sid],
    queryFn: async () => {
      const token = await getToken();
      return fetchEntitlementsSupportOverview({ scopeType: st, scopeId: sid, token: token ?? '' });
    },
    enabled,
  });
}
